import { Box, IconButton, Tooltip } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import Iconify from 'src/components/Iconify'
import { icons } from 'src/design-system'
import { liveMarkAnimation } from 'src/theme/motion'
import { MAP_EDGE_INSET, MAP_LAYER } from './consts'

// ----------------------------------------------------------------------
// Read every car's position again, without leaving the map.
//
// The map draws the fleet as it stood when the page was opened, and the only
// way to move a marker to where its car is now was to reload the console.
// Asked for on 2026-10-11: "add small refresh btn (just icon) with slightly
// transparent bg to the top right on/inside the Map ... so when that it
// clicked it refreshes the location of all vehicles shown on the map".
//
// **Alone, in the top right**, the one corner the map's furniture leaves
// free: where am I looking is top left, the legend bottom left and the
// recentre target bottom right. It asks Beast for the fleet again, which is
// each car's latest reading as Beast holds it. It wakes no car: a sleeping
// one keeps the position it last reported, and its marker says how old that
// is.
//
// **The small step, on a ground the basemap shows through.** It is asked for
// far less often than the map is read, so it is the quietest thing standing
// on it; the hairline is what keeps its boundary on a pale street and on dark
// water alike.
//
// It turns while the read is out (`liveMarkAnimation`, as the vehicle page's
// refresh does) and its name changes with it, so the state reaches somebody
// who cannot see it turn. What it found is said once it is back.
// ----------------------------------------------------------------------

/** How much of the card surface the button's ground is: the rest is the map. */
const GROUND_OPACITY = 0.72

export type MapRefreshProps = {
  /** Ask for the fleet again. Resolves when the fresh positions are drawn. */
  onRefresh: () => Promise<unknown>
}

const MapRefresh = ({ onRefresh }: MapRefreshProps) => {
  const { enqueueSnackbar } = useSnackbar()
  const [refreshing, setRefreshing] = useState(false)
  const label = refreshing
    ? 'Refreshing vehicle positions'
    : 'Refresh vehicle positions'

  const refresh = async () => {
    setRefreshing(true)
    try {
      await onRefresh()
      enqueueSnackbar('Vehicle positions refreshed', { variant: 'success' })
    } catch (failure) {
      enqueueSnackbar(
        `The positions could not be refreshed. ${
          failure instanceof Error ? failure.message : ''
        }`.trim(),
        { variant: 'error' },
      )
    } finally {
      setRefreshing(false)
    }
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        zIndex: MAP_LAYER.furniture,
        top: (theme) => theme.spacing(MAP_EDGE_INSET),
        right: (theme) => theme.spacing(MAP_EDGE_INSET),
        display: 'inline-flex',
      }}
    >
      <Tooltip title={label} placement="left">
        {/* The span keeps the tooltip while the button is disabled, which
            fires no pointer events of its own. */}
        <span>
          <IconButton
            size="small"
            aria-label={label}
            data-testid="map-refresh"
            data-refreshing={refreshing ? 'true' : undefined}
            disabled={refreshing}
            onClick={() => void refresh()}
            sx={{
              backgroundColor: (theme) =>
                alpha(theme.palette.background.paper, GROUND_OPACITY),
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Iconify
              icon={icons.refresh}
              size="dense"
              solo
              sx={refreshing ? liveMarkAnimation(icons.refresh) : undefined}
            />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  )
}

export { MapRefresh }
